import React, { ReactNodeArray } from 'react';
import styled, { css } from 'styled-components';
import { PageElementsFlowPayload } from '../../../api/pageAnalytics/uxAnalytics/mouseflow/type';
import { ReactComponent as IconFillArrowDownSVG } from '../../../assets/icons/icon-fill-arrow-down.svg';
import { uxAnalyticsState } from '../../../modules/PageAnalytics/UXAnalytics';

import errorImg from '../../../assets/images/icon-noimg@3x.png';

const IconFillArrowDown = styled(IconFillArrowDownSVG)``;

const Component = styled.div`
  width: 100%;
  height: 100%;
  padding: 24px 24px 40px 24px;
  border-radius: 16px;
  background-color: white;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const HeaderSortButton = styled.button`
  display: flex;
  align-items: center;
  height: 17px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  div {
    font-family: 'RobotoR', 'NotoKrR';
    color: ${(props) => props.theme.colors.grey500};
    font-size: 12px;
    line-height: normal;
    margin-right: 4px;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  font-family: 'RobotoB', 'NotoKrB';
  font-size: 20px;
  color: ${(props) => props.theme.colors.grey800};
  line-height: normal;
  height: 29px;
`;

const HeaderDesc = styled.div`
  display: flex;
  align-items: center;
  font-family: 'RobotoR', 'NotoKrR';
  font-size: 14px;
  color: ${(props) => props.theme.colors.grey600};
  line-height: normal;
  height: 20px;
`;

const Split = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.colors.grey200};
  margin-top: 16px;
  margin-bottom: 24px;
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TableSchemaWrpper = styled.div`
  display: flex;
  align-items: center;
  height: 54px;
`;

const TableSchemaItem = styled.div<{ $width: number }>`
  display: flex;
  align-items: center;
  font-family: 'RobotoR', 'NotoKrR';
  line-height: normal;
  font-size: 16px;
  color: ${(props) => props.theme.colors.grey500};
  height: 100%;
  width: ${(props) => props.$width}%;
  padding: 24px 0px 24px 24px;
  background-color: ${(props) => props.theme.colors.grey100};
  &:first-child {
    padding-left: 16px;
  }
`;

const Body = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const BodyItem = styled.div<{ $isHighlight: boolean }>`
  display: flex;
  align-items: center;
  height: 88px;
  ${(props) => {
    if (props.$isHighlight) {
      return css`
        background-color: #f5ab35;
      `;
    }
    return css``;
  }}
`;

const BodyItemRowItem = styled.div<{ $width: number }>`
  display: flex;
  align-items: center;
  height: 100%;
  width: ${(props) => props.$width}%;
  font-family: 'RobotoB', 'NotoKrB';
  line-height: normal;
  font-size: 16px;
  color: ${(props) => props.theme.colors.grey800};
  padding: 24px;
  &:first-child {
    justify-content: center;
    padding-left: 16px;
  }
`;

const BodyItemRowItemImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
`;

interface MouseFlowTableProps {
  pageElementData: PageElementsFlowPayload[] | null;
  targetRank: number;
  pageId: string;
  onSetTargetRank: (target_rank: number) => void;
  onSetTooltipInfoMouseFlow: (
    info: uxAnalyticsState['tooltipInfoMouseFlow']
  ) => void;
}

const MouseFlowTable = ({
  pageElementData,
  targetRank,
  pageId,
  onSetTargetRank,
  onSetTooltipInfoMouseFlow,
}: MouseFlowTableProps) => {
  const printBodyItem = () => {
    const result: ReactNodeArray = [];
    if (pageElementData && pageElementData.length > 0) {
      pageElementData.forEach((element) => {
        result.push(
          <BodyItem
            $isHighlight={targetRank === element.rank}
            key={`voda_mouseflow_table_item${element.rank}`}
            onClick={() => {
              onSetTargetRank(element.rank);
              onSetTooltipInfoMouseFlow({
                duration: element.duration,
                pathId: element.element.element_path,
                rank: element.rank,
                rate: element.rate,
              });
            }}
          >
            <BodyItemRowItem $width={10}>{element.rank}</BodyItemRowItem>
            <BodyItemRowItem $width={60}>
              <BodyItemRowItemImg
                src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
                  'voda_tenant'
                )}/auto/${pageId}/${encodeURIComponent(
                  element.element.element_path
                )}`}
                onError={(event) => {
                  const target = event.target as HTMLImageElement;
                  target.src = errorImg;
                }}
              />
            </BodyItemRowItem>
            <BodyItemRowItem $width={30}>
              {element.duration.toFixed(2)} 초
            </BodyItemRowItem>
            {/* <BodyItemRowItem $width={154}>{element.click_cnt}</BodyItemRowItem>
            <BodyItemRowItem $width={154}>
              {element.session_cnt}
            </BodyItemRowItem> */}
          </BodyItem>
        );
      });
    }
    return result;
  };
  return (
    <Component>
      <Header>
        <HeaderTitleWrapper>
          <HeaderTitle>마우스 플로우</HeaderTitle>
          {/* <HeaderSortButton>
            <div>내림차순</div>
            <IconFillArrowDown />
          </HeaderSortButton> */}
        </HeaderTitleWrapper>

        <HeaderDesc>
          사용자의 마우스 커서 움직임을 추적해 사용자의 콘텐츠 관심 순위를
          파악합니다.
        </HeaderDesc>
      </Header>
      <Split />
      <TableWrapper>
        <TableSchemaWrpper>
          <TableSchemaItem $width={10}>순서</TableSchemaItem>
          <TableSchemaItem $width={60}>콘텐츠</TableSchemaItem>
          <TableSchemaItem $width={30}>평균 체류 시간</TableSchemaItem>
          {/* <TableSchemaItem $width={154}>클릭 수</TableSchemaItem>
          <TableSchemaItem $width={154}>군집 내 사용자수</TableSchemaItem> */}
        </TableSchemaWrpper>
        <Body>{printBodyItem()}</Body>
      </TableWrapper>
    </Component>
  );
};

export default MouseFlowTable;
